import { useFormContext } from 'react-hook-form';
import type { GoToTest } from '../types';
import { usePagination } from 'hooks/usePagination';
import { useGoToTest } from '../api';
import { BaseGrid, SuspenseBoundary } from 'components/common';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash-es';
import { useListParams } from 'hooks';
import { usePermissionGuard } from 'features/login';
import { useGoToTestColumns } from '../hooks';

const GoToTestGridComponent = memo(() => {
  const { accessToChangeTiktokgototest } = usePermissionGuard();
  const navigate = useNavigate();
  const { watch } = useFormContext();
  const pagination = usePagination([], 50);
  const { setApps, apps: urlApps } = useListParams();
  const [debouncedFilters, setDebouncedFilters] = useState<{
    apps: string | undefined;
  }>({
    apps: urlApps?.join(',') ?? undefined,
  });

  const filterByApps = watch('apps') as string[];

  const debouncedUpdate = useCallback(
    (apps?: string[]) => {
      setDebouncedFilters({ apps: apps?.join(',') ?? undefined });
      setApps(apps ?? undefined);
    },
    [setDebouncedFilters, setApps],
  );

  const debouncedFn = useMemo(
    () => debounce(debouncedUpdate, 500),
    [debouncedUpdate],
  );

  useEffect(() => {
    debouncedFn(filterByApps);
    return () => {
      debouncedFn.cancel();
    };
  }, [filterByApps, debouncedFn]);

  const columns = useGoToTestColumns();

  const {
    data: { data: gototest, count },
    isLoading,
  } = useGoToTest({
    params: {
      apps: debouncedFilters.apps ?? undefined,
      limit: pagination.perPage,
      offset: pagination.page,
    },
  });

  const handleClick = useCallback(
    (id: number) => {
      accessToChangeTiktokgototest &&
      navigate(`/cabinet/tiktok/go-to-test/${id}`);
    },
    [navigate, accessToChangeTiktokgototest],
  );

  return (
    <BaseGrid<GoToTest>
      rows={gototest ?? []}
      columns={columns}
      count={count}
      isLoading={isLoading}
      pagination={pagination}
      getRowId={(row) => row.id}
      onRowClick={(params) => {
        handleClick((params.row as GoToTest).id);
      }}
    />
  );
});

GoToTestGridComponent.displayName = 'GoToTestGridComponent';

const GoToTestGrid = () => {
  return (
    <SuspenseBoundary>
      <GoToTestGridComponent />
    </SuspenseBoundary>
  );
};

export default GoToTestGrid;
